import { DepositInvoiceChoice, DepositAllocationSummary } from './DepositInvoicePayment';
import React, { useState } from 'react';
import { depositApi, formatApiError } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, Gavel, Wallet, FileArrowDown } from '@phosphor-icons/react';

export const DEPOSIT_STATUS_META = {
  hinterlegt: {
    label: 'Hinterlegt',
    badge: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  pending_review: {
    label: 'Schadenprüfung',
    badge: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  pending_refund: {
    label: 'Rückzahlung ausstehend',
    badge: 'bg-purple-50 text-purple-700 border-purple-200'
  },
  released: {
    label: 'Freigegeben',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  partial_retained: {
    label: 'Teilweise einbehalten',
    badge: 'bg-orange-50 text-orange-700 border-orange-200'
  },
  fully_retained: {
    label: 'Vollständig einbehalten',
    badge: 'bg-red-50 text-red-700 border-red-200'
  },
};

const euro = (v) =>
  `€ ${Number(v || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const DepositWorkflowActions = ({ deposit, onChanged }) => {
  const [settleOpen, setSettleOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [settleForm, setSettleForm] = useState({
    retained_amount: 0,
    reason: '',
    invoice_id: '',
    create_invoice: true
  });

  const [refundForm, setRefundForm] = useState({
    payment_method: 'bar',
    notes: ''
  });

  if (!deposit) return null;

  const status =
    deposit.status === 'zurückgegeben'
      ? 'released'
      : (deposit.status || 'hinterlegt');

  const openSettle = () => {
    setSettleForm({
      retained_amount: 0,
      reason: '',
      invoice_id: '',
    create_invoice: true
    });
    setSettleOpen(true);
  };

  const openRefund = () => {
    setRefundForm({
      payment_method: deposit.payment_method || 'bar',
      notes: ''
    });
    setRefundOpen(true);
  };

  const handleMarkReview = async () => {
    setBusy(true);
    try {
      await depositApi.markReview(deposit.id);
      toast.success('Kaution zur Schadenprüfung übergeben');
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitSettle = async () => {
    const retained = Math.max(
      0,
      Math.min(
        parseFloat(settleForm.retained_amount) || 0,
        deposit.amount
      )
    );

    setBusy(true);

    try {
      const res = await depositApi.settle(deposit.id, {
        retained_amount: retained,
        reason: settleForm.reason,
        create_invoice: settleForm.invoice_id ? false : settleForm.create_invoice,
        invoice_id: settleForm.invoice_id || null,
      });

      toast.success('Entscheidung gespeichert', {
        description: settleForm.invoice_id ? `${euro(retained)} auf Rechnung ${res.data.invoice_number} verrechnet` : res.data.invoice_number
          ? `Rechnung ${res.data.invoice_number} erzeugt`
          : retained === 0
            ? 'Rückzahlung ausstehend'
            : `Einbehalt € ${retained.toFixed(2)}`,
      });

      setSettleOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitRefund = async () => {
    setBusy(true);

    try {
      await depositApi.completeRefund(deposit.id, refundForm);
      toast.success('Rückzahlung dokumentiert');
      setRefundOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadStatement = async () => {
    try {
      const res = await depositApi.getStatementPdf(deposit.id);

      const blob = new Blob([res.data], {
        type: 'application/pdf'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `Kautionsschreiben-${deposit.id}.pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {status === 'hinterlegt' && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleMarkReview}
            disabled={busy}
            data-testid={`btn-review-${deposit.id}`}
          >
            <ShieldCheck size={14} className="mr-1" />
            Prüfen
          </Button>
        )}

        {status === 'pending_review' && (
          <Button
            size="sm"
            variant="outline"
            onClick={openSettle}
            disabled={busy}
            data-testid={`btn-settle-${deposit.id}`}
          >
            <Gavel size={14} className="mr-1" />
            Entscheiden
          </Button>
        )}

        {status === 'pending_refund' && (
          <Button
            size="sm"
            variant="outline"
            onClick={openRefund}
            disabled={busy}
            data-testid={`btn-refund-${deposit.id}`}
          >
            <Wallet size={14} className="mr-1" />
            Rückzahlen
          </Button>
        )}

        <Button
          size="sm"
          variant="outline"
          onClick={downloadStatement}
          disabled={busy}
          data-testid={`btn-pdf-${deposit.id}`}
        >
          <FileArrowDown size={14} className="mr-1" />
          PDF
        </Button>
      </div>

      <DepositAllocationSummary deposit={deposit} />
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent data-testid="settle-dialog">
          <DialogHeader>
            <DialogTitle>Kaution entscheiden</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-[#525866]">
              Kautionsbetrag:{' '}
              <span className="font-semibold text-[#111418]">
                {euro(deposit.amount)}
              </span>
            </p>

            <div>
              <Label className="overline">Einbehalt</Label>
              <Input
                type="number"
                min="0"
                max={deposit.amount}
                step="0.01"
                value={settleForm.retained_amount}
                onChange={(e) =>
                  setSettleForm({
                    ...settleForm,
                    retained_amount: e.target.value
                  })
                }
                className="input-swiss"
                data-testid="settle-retained-input"
              />
            </div>

            <div>
              <Label className="overline">Grund</Label>
              <Input
                value={settleForm.reason}
                onChange={(e) =>
                  setSettleForm({
                    ...settleForm,
                    reason: e.target.value
                  })
                }
                className="input-swiss"
                placeholder="z.B. Lackschaden"
                data-testid="settle-reason-input"
              />
            </div>

            <DepositInvoiceChoice deposit={deposit} form={settleForm} onChange={setSettleForm} />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settleForm.create_invoice}
                disabled={!!settleForm.invoice_id}
                onChange={(e) =>
                  setSettleForm({
                    ...settleForm,
                    create_invoice: e.target.checked
                  })
                }
                data-testid="settle-invoice-checkbox"
              />
              Rechnung für Einbehalt automatisch erstellen
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSettleOpen(false)}
              disabled={busy}
            >
              Abbrechen
            </Button>

            <Button
              onClick={submitSettle}
              disabled={busy}
              data-testid="settle-submit-btn"
            >
              Entscheidung speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Rückzahlung dokumentieren</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-[#525866]">
              Rückzahlung:{' '}
              <span className="font-semibold text-[#111418]">
                {euro(deposit.refund_amount)}
              </span>
            </p>

            <div>
              <Label className="overline">Zahlungsart</Label>

              <select
                className="input-swiss w-full"
                value={refundForm.payment_method}
                onChange={(e) =>
                  setRefundForm({
                    ...refundForm,
                    payment_method: e.target.value
                  })
                }
                data-testid="refund-method-select"
              >
                <option value="bar">
                  Bar (Kassenbuch-Eintrag)
                </option>
                <option value="ec">EC-Karte</option>
                <option value="ueberweisung">Überweisung</option>
              </select>
            </div>

            <div>
              <Label className="overline">
                Hinweis (optional)
              </Label>

              <Input
                value={refundForm.notes}
                onChange={(e) =>
                  setRefundForm({
                    ...refundForm,
                    notes: e.target.value
                  })
                }
                className="input-swiss"
                placeholder="z.B. Referenznr., Überweisungsdatum"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefundOpen(false)}
              disabled={busy}
            >
              Abbrechen
            </Button>

            <Button
              onClick={submitRefund}
              disabled={busy}
              data-testid="refund-submit-btn"
            >
              Rückzahlung dokumentieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DepositWorkflowActions;
